import { Controller } from "@hotwired/stimulus";
import { TabulatorFull as Tabulator } from "tabulator-tables";
import Papa from 'papaparse';

window.Tabulator = Tabulator;

export default class extends Controller {
  static targets = ["table", "input"]

  connect() {
    const inputTarget = this.inputTarget;
    const data = inputTarget.value;
    const isReconnect = this._isInitialized === true;
    this.originalValue = data;
    this.handleGlobalClick = this.handleGlobalClick.bind(this);
    const keysArray = this.getKeysArray(data);

    const div = document.createElement('div');
    this.table = new Tabulator(this.element.appendChild(div), {
      columns: keysArray,
      data: data,
      layout: "fitColumns",
      autoResize: true,
      resizableColumnFit: true,
      layoutColumnsOnNewData: true,
      placeholder: "Ingen data",
      headerSortClickElement: "icon",
      clipboard: true,
      history: true,
      rowContextMenu: this.getRowContextMenu(),
    });

    this.initializeTableEvents();

    // Only setup global click listener on first connect
    if (!isReconnect) {
      this.setupGlobalClickListener();
      this._isInitialized = true;
    } else {
      // If reconnecting (e.g., after restore), submit form to save the restored value
      setTimeout(() => {
        this.submitForm();
      }, 50);
    }
  }

  getKeysArray(data) {
    const keysArray = [];
    const parsedKeys = JSON.parse(data).shift();

    Object.keys(parsedKeys).forEach((key) => {
      keysArray.push(this.columnObject(key));
    });

    return keysArray;
  }

  columnObject(key) {
    return {
      title: key,
      field: key,
      editor: "input",
      editable: true,
      editableTitle: true,
      headerWordWrap: true,
      headerMenu: this.getHeaderMenu(key),
    };
  }

  getHeaderMenu(key) {
    return [
      {
        label: this.deleteIcon('kolumn'),
        action: (e, column) => this.handleDeleteColumn(e, column),
      },
      {
        label: this.addIcon('left'),
        action: (e, column) => this.addColumn(true, key),
      },
      {
        label: this.addIcon('right'),
        action: (e, column) => this.addColumn(false, key),
      },
      {
        label: this.addIcon('down'),
        action: (e, column) => this.addRow(),
      },
    ];
  }

  deleteIcon(type) {
    const svgElementContainer = document.getElementById("trash-icon");
    return '<div class="flex">' + svgElementContainer.innerHTML + `Ta bort ${type}</div>`;
  }

  addIcon(direction) {
    if (direction === "left") {
      return '<div>← Lägg till kolumn</div>';
    } else if (direction === 'right') {
      return '<div>→ Lägg till kolumn</div>';
    } else if (direction === 'up') {
      return '<div>↑ Lägg till rad</div>';
    } else if (direction === 'down') {
      return '<div>↓ Lägg till rad</div>';
    }
  }

  handleDeleteColumn(e, column) {
    if (this.table.getColumns().length === 1) {
      return;
    }

    const shouldDelete = window.confirm(`Radera kolumn ${column.getField()}? Detta kommer att påverka steg som läser från denna kolumn.`);
    if (shouldDelete) {
      this.table.getRows().forEach((row) => {
        const rowData = row.getData();
        delete rowData[column.getField()];
        row.update(rowData);
      });
      this.table.deleteColumn(column);
      this.setValue();
    }
  }

  addColumn(direction, in_relation_to) {
    const column = this.columnObject('');
    this.table.addColumn(column, direction, in_relation_to).then((newColumn) => {
      const editorElement = newColumn.getElement().querySelector(".tabulator-title-editor");
      editorElement.value = '';
      editorElement.focus();
      this.initializeEditorEvents(editorElement);
    });
  }

  addRow(direction, in_relation_to) {
    const columnFields = this.sortedColumn();
    const rowData = Object.fromEntries(columnFields.map(key => [key, '']));
    this.table.addData([rowData], direction, in_relation_to);
    this.setValue();
  }

  getRowContextMenu() {
    return [
      {
        label: this.deleteIcon('rad'),
        action: (e, row) => this.handleDeleteRow(e, row),
      },
      {
        label: this.addIcon('up'),
        action: (e, row) => this.addRow(true, row),
      },
      {
        label: this.addIcon('down'),
        action: (e, row) => this.addRow(false, row),
      },
    ];
  }

  handleDeleteRow(e, row) {
    const shouldDelete = window.confirm(`Radera rad ${row.getPosition()}?`);
    if (shouldDelete) {
      if (this.table.getRows().length === 1) this.addRow();
      this.table.deleteRow(row);
      this.setValue();
    }
  }

  initializeTableEvents() {
    const table = this.table;
    table.on("cellEdited", (cell) => this.setValue());
    table.on("columnTitleChanged", (column) => this.handleColumnTitleChanged(column));

    document.getElementById("history-undo").addEventListener("click", (event) => {
      event.preventDefault();
      table.undo();
      this.setValue();
      this.submitForm();
    });

    document.getElementById("history-redo").addEventListener("click", (event) => {
      event.preventDefault();
      table.redo();
      this.setValue();
      this.submitForm();
    });

    document.addEventListener('keydown', function (event) {
      if (event.key !== 'Enter') return

      event.preventDefault();
      event.target.blur();
    });

    document.getElementById("download-csv").addEventListener("click", function(event) {
      event.preventDefault();
      table.download("csv", "data.csv");
    });

    document.getElementById("download-json").addEventListener("click", function(event){
      event.preventDefault();
      table.download("json", "data.json");
    });

    const fileInput = document.getElementById("steps_data_table_import");
    fileInput.addEventListener("change", this.handleFile);
  }

  handleFile = (event) => {
    const file = event.target.files[0];
    event.target.value = ''

    const shouldImport = window.confirm("Importera fil? Detta kommer att skriva över all data i tabellen.");
    if (!shouldImport) return;

    if (file) {
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        dynamicTyping: true,
        transformHeader: header => header.trim(),
        complete: (results) => this.handleFileComplete(results),
      });
    }
  }

  handleFileComplete(results) {
    const dataArray = JSON.stringify(results.data.map(row => Object.fromEntries(Object.entries(row))));
    const keys = Object.keys(JSON.parse(dataArray).shift());
    const keysArray = keys.map(key => this.columnObject(key));

    this.table.setColumns(keysArray);
    this.table.replaceData(dataArray);
    this.setValue();
    this.submitForm();
  }

  initializeEditorEvents(editorElement) {
    editorElement.addEventListener('blur', (event) => this.handleEditorBlur(event));
  }

  handleEditorBlur(event) {
    if (this.exists(event.target.value) || event.target.value === '') {
      event.target.focus();
    }
  }

  handleColumnTitleChanged(column) {
    const newTitle = column.getDefinition().title;
    const oldTitle = column.getField();
    const editorElement = column.getElement().querySelector(".tabulator-title-editor");

    if (newTitle === '' || this.exists(newTitle)) {
      if (newTitle === '') editorElement.value = '';
      if (oldTitle !== '') this.table.updateColumnDefinition(column, { title: oldTitle });

      editorElement.focus();
      return;
    }

    if (oldTitle !== '') {
      const shouldChange = window.confirm(`Ändra namn från ${oldTitle} till ${newTitle}? Detta kommer att påverka steg som läser från denna kolumn.`);
      if (!shouldChange) {
        this.table.updateColumnDefinition(oldTitle, { title: oldTitle });
        return;
      }
    }

    this.table.addColumn(this.columnObject(newTitle), true, column);
    this.table.getRows().forEach((row) => {
      var rowData = row.getData();
      rowData[newTitle] = rowData[oldTitle] || '';
      delete rowData[oldTitle];
      row.update(rowData);
    });
    this.table.deleteColumn(column);
    this.setValue();
  }

  exists(columnName) {
    return this.table.getColumns().find((column) => column.getField() === columnName)
  }

  setValue() {
    const columnFields = this.sortedColumn();
    const dataForColumns = [];

    this.table.getData().forEach(function (row) {
      const sortedObject = Object.fromEntries(
        Object.entries(row)
          .sort(([a], [b]) => columnFields.indexOf(a) - columnFields.indexOf(b))
      );

      dataForColumns.push(sortedObject);
    });

    const asJson = JSON.stringify(dataForColumns);
    this.inputTarget.value = asJson;
  }

  /**
   * Setup global click listener to submit form when clicking outside table
   */
  setupGlobalClickListener() {
    document.addEventListener('click', this.handleGlobalClick, true)
  }

  /**
   * Handle global click events - submit form if clicking outside table and value changed
   */
  handleGlobalClick(event) {
    if (!this.table) return
    // Check if click is inside the table container
    const isInContainer = this.element.contains(event.target)
    // If click is inside table container, don't submit
    if (isInContainer) return
    // Get current value and check if it has changed from original
    const currentValue = this.inputTarget.value
    if (currentValue !== this.originalValue) {
      this.submitForm()
    }
  }

  /**
   * Submit the form when table value changes
   */
  submitForm() {
    const form = this.inputTarget.closest('form')
    if (form) {
      form.requestSubmit()
    }
  }

  sortedColumn() {
    const columns = this.table.getColumns();
    const columnFields = [];

    columns.forEach(function (column) {
      const columnField = column.getField();
      columnFields.push(columnField);
    });

    return columnFields;
  }

  /**
   * Called when the controller is disconnected from the DOM
   */
  disconnect() {
    document.removeEventListener('click', this.handleGlobalClick, true)
    if (this.table) {
      this.table.destroy()
      this.table = null
    }
  }
}
